import { alpha } from '@mui/material/styles';
// portals
import { AUTH_PORTALS, BRAND_FONT, BLACK_OLIVE } from '../../auth/portals';
// the Universe brand (DESIGN.md §2–§4)
import { UNIVERSE } from '../../../theme/universe/brand';

// ----------------------------------------------------------------------
// The sidebar's palette, in two versions.
//
// One rail, one structure, two lights: the dark version is a slice of the
// Universe night — the brand navy taken down to 6% lightness, lit by the
// brand's light blue — and the light version is the same rail in daylight,
// kept for a mode the rail does not use today. Which one you get follows
// SIDEBAR_MODE in NavbarVertical (always dark: the rail resembles the
// Universe backdrop, not the page beside it).
//
// THE COLOURS ARE THE BRAND'S (the owner, 07/10/2026: "keep it the same but
// colours more in line with the Universe brand identity"). The structure of
// 22/09 — the glass tiles, the beacon on the edge, the gradient ring on the
// avatar, the two nebulae, the star at the foot — is untouched; what changed
// is the light on it: the ground is `universe.night`, the nebula top-left is
// `universe.light`, the answering glow bottom-right is `universe.mid` (the
// gradient's midpoint), and on the Team portal the active tile, the beacon,
// the ring and the star wear the brand gradient and the light blue where
// they wore the Group indigo and violet. The client and vendor portals keep
// their own accent on the same navy ground, as their sign-in screens do.
//
// Everything is expressed as CSS custom properties set once on the drawer
// paper (see sidebarPaperSx), which is what lets the generic nav components
// in components/nav-section pick up both the mode and the portal without
// being handed either as props.
// ----------------------------------------------------------------------

export const SIDEBAR_FONT = BRAND_FONT;

// Item geometry, shared by the styles and the sub-item rail so the rail lands
// exactly under the icon tiles' centre line. The items breathe — a taller row
// and a real gap between them — so the rail reads as calm rather than crammed.
export const SIDEBAR_METRICS = {
  itemPaddingLeft: 10,
  tile: 38,
  itemHeight: 48,
  itemGap: 6,
  subItemHeight: 40
};

/** The centre line of the icon tiles, measured from the item's left edge. */
export const TILE_CENTER =
  SIDEBAR_METRICS.itemPaddingLeft + SIDEBAR_METRICS.tile / 2;

// The night, in steps: the ground is `universe.night` (the navy at 6%
// lightness — as deep as the rail always was, the owner asked for it "un
// po' più scuro com'era prima"); the rail that floats over the content on
// hover is the same navy a few shades lighter; the top of the base gradient
// one shade lighter than the ground.
const NIGHT = UNIVERSE.night; // #040B1B
const NIGHT_RAISED = '#0A1D45';
const NIGHT_HIGH = '#071430';
const NIGHT_WELL = 'rgba(4, 12, 32, 0.85)';
const NIGHT_SCRIM = 'rgba(3, 8, 22, 0.82)';

const INK = '#1E1B2E'; // the light version's tint base for glass and lines

const MODES = {
  dark: {
    '--sb-ground': NIGHT,
    // The hover-expanded rail floats over the content; a touch lighter plus a
    // long shadow is what makes it read as lifted rather than as a glitch.
    '--sb-ground-raised': NIGHT_RAISED,
    '--sb-line': 'rgba(255,255,255,0.045)',
    '--sb-glass': 'rgba(255,255,255,0.05)',
    '--sb-glass-strong': 'rgba(255,255,255,0.10)',
    '--sb-glass-border': 'rgba(255,255,255,0.09)',
    '--sb-glass-border-strong': 'rgba(255,255,255,0.16)',
    '--sb-card': 'rgba(255,255,255,0.05)',
    '--sb-card-hover': 'rgba(255,255,255,0.10)',
    '--sb-text': '#FFFFFF',
    '--sb-text-soft': 'rgba(255,255,255,0.68)',
    '--sb-text-muted': 'rgba(255,255,255,0.42)',
    '--sb-dot': 'rgba(255,255,255,0.28)',
    '--sb-active-bg': 'rgba(255,255,255,0.04)',
    '--sb-active-bg-hover': 'rgba(255,255,255,0.06)',
    '--sb-active-sub-bg': 'rgba(255,255,255,0.05)',
    '--sb-scroll': 'rgba(255,255,255,0.10)',
    '--sb-lift-shadow': '0 32px 80px rgba(2,10,30,0.65)',
    '--sb-planet-shadow': 'rgba(0,0,0,0.5)'
  },
  light: {
    '--sb-ground': '#EFEDF9',
    '--sb-ground-raised': '#FFFFFF',
    '--sb-line': alpha(INK, 0.05),
    '--sb-glass': alpha(INK, 0.04),
    '--sb-glass-strong': alpha(INK, 0.08),
    '--sb-glass-border': alpha(INK, 0.07),
    '--sb-glass-border-strong': alpha(INK, 0.14),
    // Cards are white on the tinted ground, the way paper sits on a desk.
    '--sb-card': 'rgba(255,255,255,0.78)',
    '--sb-card-hover': '#FFFFFF',
    '--sb-text': BLACK_OLIVE,
    '--sb-text-soft': alpha(BLACK_OLIVE, 0.72),
    '--sb-text-muted': alpha(BLACK_OLIVE, 0.48),
    '--sb-dot': alpha(BLACK_OLIVE, 0.28),
    '--sb-active-bg': alpha(INK, 0.03),
    '--sb-active-bg-hover': alpha(INK, 0.05),
    '--sb-active-sub-bg': alpha(INK, 0.05),
    '--sb-scroll': alpha(INK, 0.12),
    '--sb-lift-shadow': '0 32px 80px rgba(30,27,46,0.18)',
    '--sb-planet-shadow': 'rgba(30,27,46,0.28)'
  }
};

/**
 * Which portal an account type belongs to. `type` is what the auth context
 * exposes: employees are "user", clients "Client", and vendors carry their own
 * role (Linguist / Technician / Formatter / Marketeer).
 */
export function portalForType(type) {
  if (type === 'user') return AUTH_PORTALS.intranet;
  if (type === 'Client') return AUTH_PORTALS.client;
  return AUTH_PORTALS.vendor;
}

/**
 * The light a portal's rail is lit with. The Team portal is the Universe
 * itself: the brand's light blue as the beacon, the gradient's midpoint as
 * the solid, the navy as the deep step and the brand gradient on the active
 * tile and the ring. The other two portals keep the accent their sign-in
 * screen carries.
 */
export function railLightOf(portal) {
  if (portal === AUTH_PORTALS.intranet) {
    return {
      accent: UNIVERSE.mid,
      accentBright: UNIVERSE.light,
      accentDeep: UNIVERSE.navy,
      gradient: UNIVERSE.gradient
    };
  }
  return {
    accent: portal.accent,
    accentBright: portal.accentBright,
    accentDeep: portal.accentDeep,
    gradient: portal.gradient
  };
}

/**
 * The portal's colours for one mode. On the dark ground the brighter tint of
 * the accent carries the light; on the light ground the accent itself does,
 * at lower strength — a glow that reads on black smears on white.
 */
function accentVars(portal, mode) {
  const dark = mode === 'dark';
  const light = railLightOf(portal);
  const lead = dark ? light.accentBright : light.accent;
  return {
    '--sb-accent': light.accent,
    '--sb-accent-deep': light.accentDeep,
    '--sb-beacon': lead,
    // NOTE: never name a custom property `--sb-*-label`. Emotion's serializer
    // scans serialized CSS for `label:<value>` to name the class, and
    // `--sb-accent-label:#8A95D6` matches that pattern — it pulls the colour out
    // as the class label, producing an invalid selector (`css-xxx-#8A95D6`) that
    // the browser drops, taking the element's ENTIRE rule (background + every
    // var) with it. That was the "white sidebar" bug. `-ink` is a safe suffix.
    '--sb-accent-ink': dark ? light.accentBright : light.accentDeep,
    '--sb-accent-tint': alpha(light.accentBright, dark ? 0.22 : 0.14),
    '--sb-glow': alpha(lead, dark ? 0.45 : 0.35),
    '--sb-gradient': light.gradient,
    '--sb-nebula-top': alpha(lead, dark ? 0.3 : 0.16),
    '--sb-nebula-bottom': alpha(UNIVERSE.mid, dark ? 0.2 : 0.1)
  };
}

/** Every custom property the rail uses, for a portal in a mode. */
export function sidebarVars(portal, mode = 'dark') {
  return { ...(MODES[mode] || MODES.dark), ...accentVars(portal, mode) };
}

/**
 * The rail's own background: a slice of the Universe night.
 *
 * Painted with CONCRETE colours, not the custom properties, on purpose. The
 * items depend on the variables cascading to them; the paper's own fill must
 * not, or a rail whose variables fail to resolve renders flat — which is why
 * the deployed rail once looked plainer than the mockup. Here the ground, the
 * two nebulae (the portal's light top-left, the gradient's midpoint answering
 * bottom-right) and a base gradient for depth are literal values, so the
 * night always shows. Dark is `universe.night`; light is the same nebula at
 * dawn over a cool ground, kept for a mode the rail does not use today.
 */
export function sidebarBackground(portal, mode = 'dark') {
  const dark = mode === 'dark';
  const light = railLightOf(portal);
  const lead = dark ? light.accentBright : light.accent;
  const ground = dark ? NIGHT : '#EFEDF9';
  const base = dark
    ? `linear-gradient(180deg, ${NIGHT_HIGH} 0%, ${NIGHT} 58%)`
    : 'linear-gradient(180deg, #F6F5FE 0%, #E7E4F4 100%)';
  return {
    backgroundColor: ground,
    backgroundImage: [
      // Top scrim. The nebula is brightest exactly where the white lock-up
      // sits, top-left, and white on that light glow does not read. This
      // darkens the top band so the logo (and the account card) always have
      // contrast — the same fix the sign-in panel needed.
      dark
        ? `linear-gradient(180deg, ${NIGHT_SCRIM} 0%, rgba(3,8,22,0) 140px)`
        : 'linear-gradient(180deg, rgba(239,237,249,0.9) 0%, rgba(239,237,249,0) 140px)',
      // The portal's own light, top-left (the brand's light blue on the Team
      // rail), and the gradient's midpoint answering from the bottom — the
      // two colours of the brand sheet, as the two nebulae.
      `radial-gradient(680px 520px at -10% -6%, ${alpha(
        lead,
        dark ? 0.36 : 0.24
      )}, transparent 58%)`,
      `radial-gradient(560px 520px at 112% 106%, ${alpha(
        UNIVERSE.mid,
        dark ? 0.34 : 0.16
      )}, transparent 60%)`,
      // A soft well of deeper night through the middle, so the nebulae read
      // as light around it.
      dark
        ? `radial-gradient(760px 680px at 50% 54%, ${NIGHT_WELL}, transparent 72%)`
        : 'radial-gradient(760px 680px at 50% 54%, rgba(255,255,255,0.4), transparent 72%)',
      base
    ].join(', ')
  };
}

/** The drawer paper itself: the night, hairline, type, scrollbar. */
export function sidebarPaperSx(portal, mode = 'dark') {
  return {
    ...sidebarVars(portal, mode),
    ...sidebarBackground(portal, mode),
    color: 'var(--sb-text)',
    fontFamily: SIDEBAR_FONT,
    backgroundAttachment: 'local',
    // No right border. A 1px line here — even a 4.5%-white one — reads as a hard
    // "whitish line" down the seam against the dark grounds, and dropping it
    // lets MUI's default docked-drawer border (the much brighter `divider`)
    // take over, which is worse. `none` kills both; the sidebar and the
    // content ground separate on tone alone, as one night.
    borderRight: 'none',
    // The scrollbar is the only piece of chrome the scroll container adds. The
    // shared Scrollbar component paints the thumb a bright grey
    // (alpha(grey[600], 0.48)); on the dark rail, with a long menu, that thumb
    // runs almost the whole height and reads as a hard vertical line down the
    // edge. Double the selector (`&&`) so this beats that component style, and
    // make the thumb a faint hairline in the rail's own tone.
    '&& .simplebar-scrollbar:before': {
      backgroundColor: 'var(--sb-scroll)'
    },
    '&& .simplebar-track.simplebar-vertical': { width: 6 }
  };
}
